import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import {
  CreateOrUpdateOrganizationSettingsMutation,
  CreateOrUpdateOrganizationSettingsMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import SettingsLayout from "@components/settings-layout/settings-layout";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";

import updateOrganizationMutation from "../../../org-settings/graphql/update-organization-mutation";
import OrgAiBlock from "./org-ai-block";
import useAdminOrganization from "./use-admin-organization";

const AiWorkspacePage = () => {
  const label = useLabel();
  const { organization, orgId, canManageOrgAi, loading } =
    useAdminOrganization();

  const [updateOrganization] = useMutation<
    CreateOrUpdateOrganizationSettingsMutation,
    CreateOrUpdateOrganizationSettingsMutationVariables
  >(updateOrganizationMutation);

  const [enableMeetingBot, setEnableMeetingBot] = useState(false);
  const [enableMeetingSummarization, setEnableMeetingSummarization] =
    useState(false);
  const [transcribeMeetingsByDefault, setTranscribeMeetingsByDefault] =
    useState(false);
  const [askAi, setAskAi] = useState(false);

  useEffect(() => {
    if (!organization) return;
    setEnableMeetingBot(organization.enableMeetingBot ?? false);
    setEnableMeetingSummarization(
      organization.enableMeetingSummarization ?? false,
    );
    setTranscribeMeetingsByDefault(
      organization.transcribeMeetingsByDefault ?? false,
    );
    setAskAi(organization.featureFlags?.askAi ?? false);
  }, [organization]);

  if (loading && !organization) {
    return <Loading />;
  }

  // The shell already guards this route, but be defensive.
  if (!organization || !orgId || !canManageOrgAi) {
    return <Redirect to="/agent" />;
  }

  const save = async (
    variables: CreateOrUpdateOrganizationSettingsMutationVariables,
    rollback: () => void,
  ) => {
    try {
      await updateOrganization({ variables });
    } catch (e) {
      rollback();
      errorNotificationVar({
        title: "Could not save settings",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleToggleMeetingBot = (next: boolean) => {
    const previous = enableMeetingBot;
    setEnableMeetingBot(next);
    save({ organizationId: orgId, enableMeetingBot: next }, () =>
      setEnableMeetingBot(previous),
    );
  };

  const handleToggleSummarization = (next: boolean) => {
    const previous = enableMeetingSummarization;
    setEnableMeetingSummarization(next);
    save({ organizationId: orgId, enableMeetingSummarization: next }, () =>
      setEnableMeetingSummarization(previous),
    );
  };

  const handleToggleTranscribe = (next: boolean) => {
    const previous = transcribeMeetingsByDefault;
    setTranscribeMeetingsByDefault(next);
    save({ organizationId: orgId, transcribeMeetingsByDefault: next }, () =>
      setTranscribeMeetingsByDefault(previous),
    );
  };

  const handleToggleAskAi = (next: boolean) => {
    const previous = askAi;
    setAskAi(next);
    save({ organizationId: orgId, featureFlags: { askAi: next } }, () =>
      setAskAi(previous),
    );
  };

  return (
    <div aria-label="AI workspace page">
      <p className="text-sm text-gray-600 mb-8 mt-2">
        Manage how Topicflow AI works for everyone in your{" "}
        {label("organization")}: shared knowledge, an Anthropic API key, and
        workspace-wide AI features.
      </p>

      <div className="pb-8">
        <h3 className="text-base font-medium text-gray-800 mb-4">
          {label("organization", { capitalize: true })} AI features
        </h3>
        <SettingsLayout.Container>
          <SettingsLayout.Row>
            <SettingsLayout.Left label="Turns Topicflow AI on or off for everyone in this workspace.">
              Enable Topicflow AI
            </SettingsLayout.Left>
            <SettingsLayout.Right>
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={askAi}
                  onChange={handleToggleAskAi}
                  aria-label="Enable Topicflow AI toggle"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
            </SettingsLayout.Right>
          </SettingsLayout.Row>

          <SettingsLayout.Row>
            <SettingsLayout.Left label="Allow the Topicflow meeting bot to join meetings to capture notes.">
              Meeting bot
            </SettingsLayout.Left>
            <SettingsLayout.Right>
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={enableMeetingBot}
                  onChange={handleToggleMeetingBot}
                  aria-label="Enable meeting bot toggle"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
            </SettingsLayout.Right>
          </SettingsLayout.Row>

          <SettingsLayout.Row>
            <SettingsLayout.Left label="Generate AI summaries of meetings from their transcripts.">
              Meeting summarization
            </SettingsLayout.Left>
            <SettingsLayout.Right>
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={enableMeetingSummarization}
                  onChange={handleToggleSummarization}
                  aria-label="Enable meeting summarization toggle"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
            </SettingsLayout.Right>
          </SettingsLayout.Row>

          <SettingsLayout.Row>
            <SettingsLayout.Left label="Transcribe meetings automatically without asking each time.">
              Transcribe meetings by default
            </SettingsLayout.Left>
            <SettingsLayout.Right>
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={transcribeMeetingsByDefault}
                  onChange={handleToggleTranscribe}
                  aria-label="Transcribe meetings by default toggle"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
            </SettingsLayout.Right>
          </SettingsLayout.Row>
        </SettingsLayout.Container>
      </div>

      <div className="pt-8 border-t">
        <OrgAiBlock organization={organization} />
      </div>
    </div>
  );
};

export default AiWorkspacePage;
